import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { textToCues, visemeAt } from '../speech/phonemes.js';
import type { VisemeCue } from './visemes.js';
import { getVoiceEngine, resolveVoiceEngine } from '../speech/voice.js';
import type { VisemeId } from './visemes.js';

/**
 * Voz y boca del avatar.
 *
 * Recibe las frases segun las va cerrando el servidor y las reproduce en orden,
 * moviendo los labios al mismo tiempo. Dos modos:
 *
 *   con sonido  manda la frase al motor de voz, que ademas va diciendo que
 *               visema toca en cada instante, corregido con la posicion real
 *               del habla.
 *   en silencio el avatar sigue vocalizando sobre una linea de tiempo estimada,
 *               para que la interfaz no se quede congelada si el usuario apaga
 *               el sonido o el navegador no tiene voces.
 */

const MUTE_KEY = 'cec-avatar:muted';

function readMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Linea de tiempo de la frase que suena ahora. */
export interface Track {
  cues: VisemeCue[];
  /** Instante (performance.now) en que empieza a sonar. */
  t0: number;
  /** Cambia con cada frase, para que el avatar reinicie su cursor. */
  gen: number;
}

export interface Voice {
  viseme: VisemeId;
  /**
   * Pista y volumen viajan por referencia, no por estado.
   *
   * Cambian en cada fotograma: si fueran estado de React, repintarian todo el
   * arbol sesenta veces por segundo en un movil modesto. El avatar los lee
   * directamente en su bucle de dibujo.
   */
  trackRef: MutableRefObject<Track | null>;
  ampRef: MutableRefObject<number>;
  speaking: boolean;
  /** Frase que suena ahora (subtitulo en directo sobre el avatar). */
  caption: string | null;
  muted: boolean;
  /** Motor activo y voz elegida, para diagnostico. */
  engine: string;
  voiceName: string | null;
  toggleMuted: () => void;
  /** Debe llamarse desde un gesto del usuario (iOS no suena sin esto). */
  unlock: () => void;
  /** Encola una frase para hablarla. */
  enqueue: (text: string) => void;
  /** Corta inmediatamente y vacia la cola. */
  stop: () => void;
}

export function useVoice(): Voice {
  const [viseme, setViseme] = useState<VisemeId>('REST');
  const [speaking, setSpeaking] = useState(false);
  const [caption, setCaption] = useState<string | null>(null);
  const trackRef = useRef<Track | null>(null);
  const ampRef = useRef(0);
  const gen = useRef(0);

  const setAmplitude = useCallback((v: number) => {
    ampRef.current = v;
  }, []);
  const publish = useCallback((cues: VisemeCue[], t0: number) => {
    const current = trackRef.current;
    // Misma frase, reloj corregido: se conserva el numero de generacion.
    const same = current && current.cues === cues;
    trackRef.current = { cues, t0, gen: same ? current.gen : (gen.current += 1) };
  }, []);
  const [muted, setMuted] = useState(readMuted);

  const queue = useRef<string[]>([]);
  const running = useRef(false);
  const mutedRef = useRef(muted);
  const frame = useRef(0);
  const engine = useRef(getVoiceEngine());
  const [engineName, setEngineName] = useState(engine.current.name);

  // Al arrancar se pregunta al servidor si hay voz de produccion configurada.
  useEffect(() => {
    let active = true;
    void resolveVoiceEngine().then((chosen) => {
      if (!active) return;
      engine.current = chosen;
      setEngineName(chosen.name);
      setVoiceName(chosen.voiceName());
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    mutedRef.current = muted;
    try {
      window.localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    } catch {
      /* sin almacenamiento disponible */
    }
    if (muted) engine.current.cancel();
  }, [muted]);

  /** Vocaliza una frase sin sonido, sobre duracion estimada. */
  const mime = useCallback(
    (text: string) =>
      new Promise<void>((resolve) => {
        const total = text.length * 58;
        const cues = textToCues(text, total);
        const start = performance.now();
        publish(cues, start);
        let energy = 0.8;

        const tick = () => {
          const elapsed = performance.now() - start;
          if (elapsed > total + 140) {
            cancelAnimationFrame(frame.current);
            setViseme('REST');
            setAmplitude(0);
            return resolve();
          }
          const id = visemeAt(cues, elapsed);
          setViseme(id);
          // Envolvente de volumen. NO puede ser un seno: un seno hace latir la boca
          // con un ritmo fijo, independiente de lo que se dice, y eso se percibe de
          // inmediato como algo mecanico. Sin audio real se usa un paseo aleatorio
          // lento, de modo que la variacion expresiva la aporten los visemas, que
          // si cambian con el texto.
          energy += (Math.random() - 0.5) * 0.09;
          energy = Math.min(1, Math.max(0.62, energy));
          setAmplitude(id === 'REST' ? 0.1 : energy);
          frame.current = requestAnimationFrame(tick);
        };
        frame.current = requestAnimationFrame(tick);
      }),
    [],
  );

  const drain = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    setSpeaking(true);

    while (queue.current.length > 0) {
      const next = queue.current.shift();
      if (!next) continue;
      setCaption(next);

      // Sin sonido (silenciado, o un navegador sin ningun motor de voz): el
      // avatar vocaliza igualmente la frase, para no quedarse quieto.
      if (mutedRef.current || engine.current.name === 'silent' || !engine.current.available()) {
        await mime(next);
      } else {
        const handlers = { onTrack: publish, onViseme: setViseme, onAmplitude: setAmplitude };
        try {
          await engine.current.speak(next, handlers);
        } catch {
          // Si la voz de produccion falla en esta frase, se dice con la del
          // navegador en lugar de dejar un silencio en mitad de la respuesta.
          await getVoiceEngine().speak(next, handlers);
        }
      }
      // Respiracion entre frases: sin esta pausa el discurso sale atropellado.
      await new Promise((r) => window.setTimeout(r, 90));
    }

    running.current = false;
    setSpeaking(false);
    setCaption(null);
    setViseme('REST');
    setAmplitude(0);
    trackRef.current = null;
  }, [mime, publish, setAmplitude]);

  const enqueue = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      queue.current.push(trimmed);
      // Su audio se pide YA, mientras aun suena la frase anterior.
      if (!mutedRef.current) engine.current.prefetch?.(trimmed);
      void drain();
    },
    [drain],
  );

  const stop = useCallback(() => {
    queue.current = [];
    running.current = false;
    cancelAnimationFrame(frame.current);
    engine.current.cancel();
    setSpeaking(false);
    setCaption(null);
    setViseme('REST');
    setAmplitude(0);
    trackRef.current = null;
  }, [setAmplitude]);

  const [voiceName, setVoiceName] = useState<string | null>(null);

  const unlock = useCallback(() => {
    engine.current.unlock();
    // La lista de voces llega de forma asincrona: se consulta un momento despues.
    window.setTimeout(() => setVoiceName(engine.current.voiceName()), 400);
  }, []);
  const toggleMuted = useCallback(() => setMuted((m) => !m), []);

  useEffect(() => () => {
    cancelAnimationFrame(frame.current);
    engine.current.cancel();
  }, []);

  return {
    viseme,
    trackRef,
    ampRef,
    speaking,
    caption,
    muted,
    engine: engineName,
    voiceName,
    toggleMuted,
    unlock,
    enqueue,
    stop,
  };
}
